import {MerchantConfiguration} from './../../reducers/merchantConfigurationReducer';
import {loadScript, type PayPalNamespace} from '@paypal/paypal-js';
import {FeatureFlag} from '../../../../@type/features';
import {type IPaymentData} from '../../../../native-checkout/ts/checkout';
import {type GatewayConfiguration} from '../../models/GatewayConfiguration';
import {Feature} from '../../reducers/environmentReducer';
import {registerGatewayBatch} from '../../reducers/paymentConfigurationReducer';
import {store} from '../../store';
import {getLocaleText} from '../../util/translation';
import {type OrderService} from '../order';
import {setupPayPalButtonRendering} from './button';
import {initPayPalCreditMethod} from './paypal-credit';
import {initPayPalPayLaterMethod} from './paypal-paylater';
import {initPayPalVenmoMethod} from './paypal-venmo';
import {initPayPalMethod} from './paypal-wallet';
import {captureSentryException} from '../../../../@shared/ts/sentry';

export default async function initPayPalPaymentIntegration(orderService: OrderService): Promise<void> {
	if (!Feature.enabled(FeatureFlag.PAYPAL_GATEWAYS)) {
		return;
	}

	const clientId = Feature.metadata<string>(FeatureFlag.PAYPAL_GATEWAYS, 'client_id');
	const merchantId = Feature.metadata<string>(FeatureFlag.PAYPAL_GATEWAYS, 'merchant_id');
	const partnerAttributionId = Feature.metadata<string>(FeatureFlag.PAYPAL_GATEWAYS, 'partner_attribution_id');

	if (!clientId || !merchantId || !partnerAttributionId) {
		return;
	}

	const gateways = registerPayPalGateways();
	setupPayPalButtonRendering(gateways);

	const presentmentCurrencies = new Set(['USD', 'AUD', 'BRL', 'CAD', 'CZK', 'DKK', 'EUR', 'HKD', 'HUF', 'ILS', 'JPY', 'MYR', 'MXN', 'TWD', 'NZD', 'NOK', 'PHP', 'PLN', 'GBP', 'RUB', 'SGD', 'SEK', 'CHF', 'THB']);
	let previousCurrency = '';
	store.subscribe(async () => {
		const currentCurrency = MerchantConfiguration.currency.code();
		if (currentCurrency === previousCurrency) {
			return;
		}

		previousCurrency = currentCurrency;

		// Reset all PayPal payment methods to there default state when the currency changes.
		for (const [_, gateway] of Object.entries(gateways)) {
			gateway.reset();
			gateway.state = undefined;
		}

		// If the new currency is not a supported presentment currency then do not reinitialize payment methods.
		if (!presentmentCurrencies.has(currentCurrency)) {
			return;
		}

		try {
			const paypal = await loadScript({
				'client-id': clientId,
				'merchant-id': merchantId,
				'data-partner-attribution-id': partnerAttributionId,
				'enable-funding': 'venmo',
				currency: MerchantConfiguration.currency.code(),
				'data-page-type': 'checkout',
			});

			if (!paypal) {
				return;
			}

			await activatePayPalGateways(paypal, gateways, orderService);
		} catch (error) {
			captureSentryException(new Error('PayPal SDK failed to load'), {
				error,
			});
		}
	});
}

function registerPayPalGateways(): Record<string, PayPalGateway> {
	const gatewayImplementations: Record<string, PayPalGateway> = {};
	const gatewayConfigurations: Record<string, GatewayConfiguration> = {};

	if (Feature.enabled(FeatureFlag.PAYPAL_WALLET_GATEWAY)) {
		const method = initPayPalMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	if (Feature.enabled(FeatureFlag.PAYPAL_VENMO_GATEWAY)) {
		const method = initPayPalVenmoMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	if (Feature.enabled(FeatureFlag.PAYPAL_PAYLATER_GATEWAY)) {
		const method = initPayPalPayLaterMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	if (Feature.enabled(FeatureFlag.PAYPAL_CREDIT_GATEWAY)) {
		const method = initPayPalCreditMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	store.dispatch(registerGatewayBatch(gatewayConfigurations));

	return gatewayImplementations;
}

async function activatePayPalGateways(paypal: PayPalNamespace, gateways: Record<string, PayPalGateway>, orderService: OrderService) {
	const activations = [];
	for (const [_, gateway] of Object.entries(gateways)) {
		if (gateway.activate) {
			activations.push(gateway.activate(paypal, orderService));
		}
	}

	await Promise.all(activations);
}

function paypalDescription(title: string, icon: string, description: string) {
	return /* html */ `
    <img style="display: block; text-align: left; height: 1.7rem; " src="${icon}">
    <p style="text-align: left; margin: 0.5rem 0;">${title} ${getLocaleText('selected for checkout.')}</p>
    <hr>
    <p style="text-align: left; margin: 0.5rem 0 0;" class="muted">${description}</p>`;
}

/**
 * Implementation type for a PayPal gateway
 */
interface PayPalGateway {
	state?: {
		mountTarget: string;
	};
	config: GatewayConfiguration;
	activate(paypal: PayPalNamespace, orderService: OrderService): Promise<void> | void;
	reset(): void;
	attach(baseTarget: string): void;
	detach(): void;
}

interface PayPalPaymentData extends IPaymentData {
	'data': {
		'id': string;
	};
}

export {
	paypalDescription,
	type PayPalGateway,
	type PayPalPaymentData,
};
